"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/navbar";
import { Footer } from "@/components/footer";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader } from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  ArrowLeft,
  AlertCircle,
  Percent,
  Users,
  Cake,
  Mail,
  Calendar,
  FileText,
  User,
} from "lucide-react";
import { apiClient, type HistorialSolicitud } from "@/lib/api";

import {
  AccordionContent,
  AccordionItem,
  Accordion,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Skeleton } from "../../components/ui/skeleton";

import { useConsentStore } from "@/lib/stores/consentimiento-store";
import { useAuthStore } from "../lib/stores/auth-store";
import { usePolizasCacheStore } from "../lib/stores/polizas-cache-store";
import { useBeneficiariosStore } from "../../lib/stores/beneficiarios-store";

export default function HistorialPage() {
  const baseUrl = import.meta.env.VITE_BASE_URL;
  const navigate = useNavigate();
  const asegurado = useAuthStore((state) => state.asegurado);
  const [historial, setHistorial] = useState<HistorialSolicitud[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!asegurado) {
      navigate("/");
      return;
    }
    loadHistorial(asegurado.rut);
  }, [asegurado, navigate]);

  const loadHistorial = async (rut: number) => {
    setLoading(true);
    setError("");

    const response = await apiClient.getHistorial(rut);

    setLoading(false);

    if (response.success) {
      if (response.data && response.data.length > 0) {
        setHistorial(response.data);
      } else {
        setError("No existen solicitudes anteriores");
      }
    } else {
      setError(response.message || "Error al cargar historial");
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const [year, month, day] = dateString.split("-");
    const date = new Date(
      Number.parseInt(year ?? "0"),
      Number.parseInt(month ?? "1") - 1,
      Number.parseInt(day ?? "1")
    );
    return date.toLocaleDateString("es-CL", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-CL", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionBadge = (accion: string) => {
    const styles = {
      Agregado: "bg-green-100 text-green-800 border-green-200",
      Modificado: "bg-blue-100 text-blue-800 border-blue-200",
      Eliminado: "bg-red-100 text-red-800 border-red-200",
    };
    return (
      styles[accion as keyof typeof styles] ||
      "bg-gray-100 text-gray-800 border-gray-200"
    );
  };

  const getTipoBadge = (tipo: string) => {
    const styles = {
      Titular:
        "bg-[var(--color-z-blue)]/10 text-[var(--color-z-blue)] border-[var(--color-z-blue)]/20",
      Contingente: "bg-purple-100 text-purple-800 border-purple-200",
    };
    return (
      styles[tipo as keyof typeof styles] ||
      "bg-gray-100 text-gray-800 border-gray-200"
    );
  };

  const handleLogout = () => {
    navigate(`${baseUrl}/login`);

    setTimeout(() => {
      const { clearAuth } = useAuthStore.getState();
      const { clearCache } = usePolizasCacheStore.getState();
      const { clearBeneficiarios } = useBeneficiariosStore.getState();
      const { clearConsent } = useConsentStore.getState();

      clearAuth();
      clearCache();
      clearBeneficiarios();
      clearConsent();
      localStorage.setItem("beneficiarios", "");
    }, 100);
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar onLogoutClick={handleLogout} />
      <main className="flex-1 bg-gradient-to-br from-[var(--color-dove)] to-[var(--color-z-white)] px-4 py-8">
        <div className="mx-auto max-w-5xl">
          <div className="mb-6">
            <Button
              onClick={() => navigate(`${baseUrl}/polizas`)}
              className="mb-4 gap-2 bg-[var(--color-z-blue)] hover:bg-[var(--color-z-blue-dark)]"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver a Pólizas
            </Button>
            <h1 className="text-3xl font-bold text-[var(--color-z-blue)]">
              Historial de Solicitudes
            </h1>
            <p className="mt-2 text-muted-foreground">
              Revisa tus solicitudes anteriores de beneficiarios
            </p>
          </div>

          {error && !loading && (
            <Alert className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardHeader>
                    <Skeleton className="h-6 w-1/3" />
                    <Skeleton className="h-4 w-1/4" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : historial.length > 0 ? (
            <Accordion type="single" collapsible className="space-y-4">
              {historial.map((solicitud, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="rounded-lg border border-border bg-card shadow-sm"
                >
                  <AccordionTrigger className="px-6 hover:no-underline">
                    <div className="flex flex-1 items-center justify-between pr-4 text-left">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-light-blue)]/20">
                          <FileText className="h-5 w-5 text-[var(--color-z-blue)]" />
                        </div>
                        <div>
                          <p className="font-semibold text-[var(--color-z-blue)]">
                            Póliza N° {solicitud.numeroPoliza}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {solicitud.beneficiarios.length} beneficiario(s)
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        {formatDateTime(solicitud.fechaSolicitud)}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <div className="space-y-4 pt-4">
                      {solicitud.beneficiarios.map((beneficiario, bIndex) => (
                        <div
                          key={bIndex}
                          className="rounded-lg border border-border bg-gradient-to-br from-background to-muted/30 p-5 shadow-sm transition-shadow hover:shadow-md"
                        >
                          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-z-blue)]/10">
                                <User className="h-5 w-5 text-[var(--color-z-blue)]" />
                              </div>
                              <div>
                                <p className="font-semibold text-[var(--color-z-blue)]">
                                  {beneficiario.nombre}{" "}
                                  {beneficiario.apellidoPaterno}{" "}
                                  {beneficiario.apellidoMaterno}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {beneficiario.rut}-{beneficiario.dv}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <span
                                className={`rounded-full border px-3 py-1 text-xs font-medium ${getActionBadge(
                                  beneficiario.accion
                                )}`}
                              >
                                {beneficiario.accion}
                              </span>
                              <span
                                className={`rounded-full border px-3 py-1 text-xs font-medium ${getTipoBadge(
                                  beneficiario.tipo
                                )}`}
                              >
                                {beneficiario.tipo}
                              </span>
                            </div>
                          </div>

                          <div className="grid gap-4 text-sm md:grid-cols-2">
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <span className="text-muted-foreground">
                                  Parentesco:
                                </span>{" "}
                                <span className="font-medium text-[var(--color-z-blue)]">
                                  {beneficiario.parentesco}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <Percent className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <span className="text-muted-foreground">
                                  Porcentaje:
                                </span>{" "}
                                <span className="font-medium text-[var(--color-z-blue)] ">
                                  {beneficiario.porcentaje}%
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <Cake className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <span className="text-muted-foreground">
                                  Fecha de Nacimiento:
                                </span>{" "}
                                <span className="font-medium text-[var(--color-z-blue)] ">
                                  {beneficiario.fechaNacimiento}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              <div className="truncate">
                                <span className="text-muted-foreground">
                                  Email:
                                </span>{" "}
                                <span className="font-medium text-[var(--color-z-blue)]">
                                  {beneficiario.email}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : null}
        </div>
      </main>
      <Footer />
    </div>
  );
}
